import { useQuery } from "@tanstack/react-query"
import { apiGET } from "data/api/get"
import { useMemo } from "react"
import { Sparklines, SparklinesLine } from "react-sparklines"
import { twMerge } from "tailwind-merge"

function getMetricUrl(metricSlug: string, projectSlug: string): string {
  const search = new URLSearchParams([
    ["project_slugs", projectSlug],
    ["interval", "30d"],
  ])

  return `/v3/internal/charts/metrics/${metricSlug}?${search}`
}

export const useSparklineData = ({
  metricSlug,
  projectSlug,
}: {
  metricSlug: string
  projectSlug: string
}) => {
  return useQuery({
    queryKey: ["sparkline-data", metricSlug, projectSlug],
    queryFn: async (): Promise<number[]> => {
      const result = await apiGET(
        getMetricUrl(
          metricSlug,
          projectSlug
        ) as "/v3/internal/charts/metrics/:slug"
      )
      return [...result.data].reverse().map((d) => d.value)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!metricSlug && !!projectSlug,
  })
}

const CustomGradientSparkline = ({
  data,
  isPositive,
  height,
  width,
  strokeWidth = 1,
}: {
  data: number[]
  isPositive: boolean
  height?: number | string
  width?: number | string
  strokeWidth?: number
}) => (
  <Sparklines data={data} style={{ height, width }}>
    <SparklinesLine
      style={{
        stroke: isPositive ? "hsl(var(--emerald-500))" : "hsl(var(--red-500))",
        strokeWidth,
        fillOpacity: 0.25,
        fill: `url(#sparkline-gradient-${isPositive ? "positive" : "negative"})`,
      }}
    />
    <defs>
      <linearGradient
        id={`sparkline-gradient-positive`}
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop offset="0%" stopColor="hsl(var(--emerald-500))" stopOpacity={1} />
        <stop
          offset="100%"
          stopColor="hsl(var(--emerald-500))"
          stopOpacity={0}
        />
      </linearGradient>
      <linearGradient
        id={`sparkline-gradient-negative`}
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop offset="0%" stopColor="hsl(var(--red-500))" stopOpacity={1} />
        <stop offset="100%" stopColor="hsl(var(--red-500))" stopOpacity={0} />
      </linearGradient>
    </defs>
  </Sparklines>
)

export const MiniChartSparkline = ({
  value,
  projectSlug,
  metric: metricSlug,
  height,
  strokeWidth,
  width,
}: {
  value: number
  projectSlug: string
  metric: string
  strokeWidth?: number
  height?: number | string
  width?: number | string
}) => {
  const { data: data = [] } = useSparklineData({ metricSlug, projectSlug })
  if (typeof value !== "number" || value === 0) return null

  return (
    <div className="flex gap-2 items-center justify-between w-full sparkline-container">
      <CustomGradientSparkline
        data={data}
        height={height}
        width={width}
        isPositive={value > 0}
        strokeWidth={strokeWidth}
      />
    </div>
  )
}

export const MiniChartInlineSparkline = ({
  value,
  height,
  width,
  className,
  strokeWidth,
}: {
  value: number[]
  height?: number | string
  width?: number | string
  className?: string
  strokeWidth?: number
}) => {
  const data = useMemo(() => {
    return (value ?? []).toReversed()
  }, [value])

  if (value?.length === 0) return null

  const isPositive = data[0] < data[data.length - 1]

  return (
    <div
      className={twMerge(
        "relative w-full flex justify-end items-end sparkline-container",
        className
      )}
    >
      <CustomGradientSparkline
        data={data}
        height={height}
        width={width}
        isPositive={isPositive}
        strokeWidth={strokeWidth}
      />
    </div>
  )
}
